import { apiFetch } from '@/shared/api/client';
import type { ResumenRecepcionesFields } from '@/shared/api/controllers/resumenRecepciones';
import type { DetalleRecepcionFields } from '@/shared/api/controllers/detalleRecepcion';
import type { ComprasFields } from '@/shared/api/controllers/compras';

/**
 * Recepción API wrappers. Module scope (user decision):
 * receipt-tracking only — no stock mutations (Inventario handles that
 * separately via manual Swap), no invoice OCR.
 */

export type RecepcionStatus = 'Pendiente' | 'Recibido' | 'Anulado';
export type RecepcionStatusFilter = 'all' | RecepcionStatus;

export type RecepcionRow = {
  id: string;
  fields: ResumenRecepcionesFields;
  compra: {
    id: string;
    proveedor: string;
    fechaFactura: string;
    nroFactura: string;
    statusCO: string;
  } | null;
};

export type ListRecepcionesParams = {
  status?: RecepcionStatusFilter;
  search?: string;
};

export type ListRecepcionesResponse = {
  rows: RecepcionRow[];
  totalRowCount: number;
};

export async function listRecepciones(
  params: ListRecepcionesParams = {},
): Promise<ListRecepcionesResponse> {
  const query: Record<string, string> = {};
  if (params.status && params.status !== 'all') query.status = params.status;
  if (params.search) query.search = params.search;
  return apiFetch<ListRecepcionesResponse>('/api/recepcion', { query });
}

// --------------------------------------------------------------------------
// Detail
// --------------------------------------------------------------------------

export type DetalleRow = {
  id: string;
  fields: DetalleRecepcionFields;
  cantidadEsperada: number;
  cantidadRecibida: number;
  modelo: string;
  llevaNroSerie: boolean;
};

export type RecepcionDetailResponse = {
  header: {
    id: string;
    fields: ResumenRecepcionesFields;
    cantidadEsperada: number;
    cantidadRecibida: number;
  };
  compra: {
    id: string;
    fields: ComprasFields;
  } | null;
  detalles: DetalleRow[];
};

export async function getRecepcionDetail(id: string): Promise<RecepcionDetailResponse> {
  return apiFetch<RecepcionDetailResponse>(`/api/recepcion/${encodeURIComponent(id)}`);
}

// --------------------------------------------------------------------------
// Crear (from compra)
// --------------------------------------------------------------------------

export type CompraDisponible = {
  id: string;
  proveedor: string;
  fecha: string;
  nroFactura: string;
  montoTotal: string;
  statusCO: string;
  /** Existing recepciones against this compra (for duplicate warning). */
  recepcionCount: number;
};

export async function listComprasDisponibles(
  search?: string,
): Promise<CompraDisponible[]> {
  const query: Record<string, string> = {};
  if (search) query.search = search;
  const res = await apiFetch<{ rows: CompraDisponible[] }>(
    '/api/recepcion/compras-disponibles',
    { query },
  );
  return res.rows;
}

export type CrearRecepcionResponse =
  | { outcome: 'created'; recepcionId: string; detalleCount: number }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'no_lines'; message: string };

export async function crearRecepcion(
  compraId: string,
): Promise<CrearRecepcionResponse> {
  return apiFetch<CrearRecepcionResponse>('/api/recepcion', {
    method: 'POST',
    body: { compraId },
  });
}

// --------------------------------------------------------------------------
// Recibir / Finalizar / Anular
// --------------------------------------------------------------------------

export type RecibirPayload = {
  nroDespacho?: string;
  detalles: Array<{
    detalleId: string;
    cantidadRecibida: number;
    nroSerie?: string;
  }>;
  finalize: boolean;
};

export type RecibirResponse =
  | { outcome: 'updated'; recepcionId: string; finalized: boolean }
  | { outcome: 'blocked_status'; message: string };

export async function recibirRecepcion(
  id: string,
  payload: RecibirPayload,
): Promise<RecibirResponse> {
  return apiFetch<RecibirResponse>(
    `/api/recepcion/${encodeURIComponent(id)}/recibir`,
    { method: 'POST', body: payload },
  );
}

export type FinalizarRecepcionResponse =
  | { outcome: 'finalized'; recepcionId: string }
  | { outcome: 'blocked_status'; message: string };

export async function finalizarRecepcion(
  id: string,
): Promise<FinalizarRecepcionResponse> {
  return apiFetch<FinalizarRecepcionResponse>(
    `/api/recepcion/${encodeURIComponent(id)}/finalizar`,
    { method: 'POST' },
  );
}

export type AnularDetalleResponse =
  | {
      outcome: 'anulado';
      detalleId: string;
      /**
       * True when this was the LAST active line — the backend also
       * flipped the recepción header to Anulado in the same transaction.
       * The dialog uses it to switch the success toast and close.
       */
      recepcionAnulada?: boolean;
    }
  | { outcome: 'already_anulado' };

export async function anularDetalleRecepcion(
  recepcionId: string,
  detalleId: string,
): Promise<AnularDetalleResponse> {
  return apiFetch<AnularDetalleResponse>(
    `/api/recepcion/${encodeURIComponent(recepcionId)}/detalle/${encodeURIComponent(detalleId)}/anular`,
    { method: 'POST' },
  );
}
